import * as THREE from "three";
import { GAME } from "../config/gameConfig";
import { VISUAL } from "../config/visualConfig";
import { cellsFor, isSpanning } from "../game/Pieces";
import type { ActivePiece, GameEvent, Layer } from "../game/types";
import { squareFrameGeometry } from "./squareFrameGeometry";
import type { Game } from "../game/Game";
import { layerZ } from "./layerLayout";

type Spark = { position: THREE.Vector3; velocity: THREE.Vector3; color: THREE.Color; life: number; duration: number; size: number };
type Flash = { mesh: THREE.Mesh; life: number; duration: number; expand: boolean };

export class Effects {
  readonly group = new THREE.Group();
  private readonly sparks: Spark[] = [];
  private readonly flashes: Flash[] = [];
  private readonly sparkGeometry = new THREE.OctahedronGeometry(1);
  private readonly beamGeometry = new THREE.PlaneGeometry(1, 1);
  private readonly ringGeometry = new THREE.RingGeometry(0.92, 1, 48);
  private readonly squareGeometry = squareFrameGeometry(0.92);
  private readonly pool = new THREE.InstancedMesh(
    this.sparkGeometry,
    new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false }),
    320,
  );
  private readonly matrix = new THREE.Matrix4();

  constructor(private readonly game: Game) {
    this.pool.count = 0;
    this.pool.frustumCulled = false;
    this.pool.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.group.add(this.pool);
  }

  handle(events: readonly GameEvent[]): void {
    for (const event of events) {
      if (event.type === "line-clear") {
        const z = this.z(event.layer);
        const color = event.layer === "front" ? VISUAL.front : VISUAL.back;
        for (const row of event.rows) {
          const y = this.y(row);
          this.flash(new THREE.Vector3(0, y, z), color, GAME.width + 0.3, 0.2, 0.4, false);
          this.flash(new THREE.Vector3(0, y, z), color, 1, 1, 0.5, true, true);
          for (let i = 0; i < 72; i++) this.spark(new THREE.Vector3((Math.random() - 0.5) * GAME.width, y, z), color, 4, 0.6);
        }
      } else if (event.type === "lock") {
        const color = isSpanning(event.piece.type) ? VISUAL.spanning : event.piece.layer === "front" ? VISUAL.front : VISUAL.back;
        for (const point of this.points(event.piece)) {
          this.flash(point, color, 0.6, 0.6, 0.26, true);
          for (let i = 0; i < 8; i++) this.spark(point, color, 2.4, 0.3);
        }
      } else if (event.type === "drop") {
        const color = isSpanning(event.piece.type) ? VISUAL.spanning : event.piece.layer === "front" ? VISUAL.front : VISUAL.back;
        if (event.hard) {
          const length = Math.min(GAME.visibleHeight, event.piece.y - event.fromY);
          if (length > 0) for (const point of this.points(event.piece)) {
            const visibleLength = Math.max(0, Math.min(GAME.visibleHeight / 2, point.y + length) - point.y);
            const middle = point.clone(); middle.y += visibleLength / 2;
            if (visibleLength > 0) this.flash(middle, color, 0.3, visibleLength, 0.2, false);
          }
        } else for (const point of this.points(event.piece)) this.spark(point, color, 0.5, 0.13);
      } else if (event.type === "move" || event.type === "rotate" || event.type === "layer-switch") {
        const color = isSpanning(event.piece.type) ? VISUAL.spanning : event.piece.layer === "front" ? VISUAL.front : VISUAL.back;
        const points = this.points(event.piece);
        for (const point of points) {
          for (let i = 0; i < (event.type === "layer-switch" ? 6 : 2); i++) this.spark(point, color, 1, 0.22);
        }
        if (event.type !== "move" && points[0]) {
          const center = points.reduce((sum, point) => sum.add(point), new THREE.Vector3()).multiplyScalar(1 / points.length);
          this.flash(center, color, 1, 1, 0.24, true);
        }
      } else if (event.type === "start" || event.type === "level-up") {
        this.flash(new THREE.Vector3(0, 0, 0), VISUAL.front, 2, 2, 0.65, true);
      }
    }
  }

  update(delta: number): void {
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const spark = this.sparks[i]!;
      spark.life -= delta;
      if (spark.life <= 0) { this.sparks.splice(i, 1); continue; }
      spark.position.addScaledVector(spark.velocity, delta);
      spark.velocity.multiplyScalar(Math.exp(-delta * 2));
      // Keep effects inside the depth range confirmed on the Go.
      spark.position.z = THREE.MathUtils.clamp(spark.position.z, -1.5, 1.5);
    }
    this.sparks.forEach((spark, index) => {
      const size = spark.size * Math.min(1, spark.life / spark.duration * 2);
      this.matrix.makeScale(size, size, size); this.matrix.setPosition(spark.position);
      this.pool.setMatrixAt(index, this.matrix);
      this.pool.setColorAt(index, spark.color.clone().multiplyScalar(spark.life / spark.duration));
    });
    this.pool.count = this.sparks.length;
    this.pool.instanceMatrix.needsUpdate = true;
    if (this.pool.instanceColor) this.pool.instanceColor.needsUpdate = true;
    for (let i = this.flashes.length - 1; i >= 0; i--) {
      const flash = this.flashes[i]!;
      flash.life -= delta;
      if (flash.life <= 0) {
        this.group.remove(flash.mesh);
        (flash.mesh.material as THREE.Material).dispose();
        this.flashes.splice(i, 1); continue;
      }
      (flash.mesh.material as THREE.MeshBasicMaterial).opacity = (flash.life / flash.duration) ** 2 * 0.65;
      if (flash.expand) flash.mesh.scale.multiplyScalar(Math.exp(delta * 5));
    }
  }

  private flash(position: THREE.Vector3, color: number, width: number, height: number, duration: number, expand: boolean, square = false): void {
    if (this.flashes.length >= 28) return;
    const material = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.65, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
    const mesh = new THREE.Mesh(expand ? (square ? this.squareGeometry : this.ringGeometry) : this.beamGeometry, material);
    mesh.position.copy(position); mesh.position.z += 0.05;
    mesh.scale.set(width, height, 1);
    this.flashes.push({ mesh, life: duration, duration, expand });
    this.group.add(mesh);
  }

  private spark(position: THREE.Vector3, color: number, speed: number, duration: number): void {
    if (this.sparks.length >= 320) return;
    this.sparks.push({ position: position.clone(), velocity: new THREE.Vector3((Math.random() - 0.5) * speed * 2, (Math.random() - 0.5) * speed * 2, (Math.random() - 0.5) * speed * 0.5), color: new THREE.Color(color), life: duration, duration, size: 0.025 + Math.random() * 0.05 });
  }

  private points(piece: ActivePiece): THREE.Vector3[] {
    const layers: Layer[] = isSpanning(piece.type) ? ["front", "back"] : [piece.layer];
    return layers.flatMap(layer => cellsFor(piece.type, piece.rotation).filter(cell => piece.y + cell.y >= GAME.hiddenRows).map(cell => new THREE.Vector3(piece.x + cell.x - GAME.width / 2 + 0.5, this.y(piece.y + cell.y), this.z(layer))));
  }
  private y(row: number): number { return GAME.visibleHeight / 2 - (row - GAME.hiddenRows) - 0.5; }
  private z(layer: Layer): number { return layerZ(this.game, layer); }
}
